import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, Container, Row, Col, Button, Alert } from "react-bootstrap";
import {
  CalendarDate,
  Lightning,
  CurrencyDollar,
  ExclamationTriangle,
  Calendar,
  CreditCard,
  FileEarmark,
  Person,
} from "react-bootstrap-icons";
import { ArrowLeft } from "react-bootstrap-icons";
import "./PaymentSelection.module.css";
import PopupWrapper from "../../components/PopupWrapper";

const PaymentSelection = () => {
  const [isChecked, setIsChecked] = useState(true);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const nsfData = location.state?.nsfData;
  // console.log({ nsfData });

  useEffect(() => {
    if (isChecked) {
      setShowDisclaimer(false);
    } else {
      setShowDisclaimer(true);
    }
  }, [isChecked]);

  // Extract data from nsfData
  const borrowerName = nsfData?.borrower || "Borrower";
  const loanNumber = nsfData?.loanNumber || "N/A";
  const failedAmount = nsfData?.amount;
  const maskedAccount = nsfData?.BankNumber
    ? `****${String(nsfData.BankNumber).slice(-4)}`
    : "N/A";
  const failureDate = nsfData?.dateFormatted || "N/A";
  const nextDueDate = nsfData?.nextDueDateFormatted || "N/A";
  const nsfFee =
    typeof nsfData?.nsfFee === "number" && nsfData?.nsfFee > 0
      ? nsfData.nsfFee
      : 45;

  return (
    <section
      className="rounded"
      style={{ background: "#eee", minHeight: "100vh" }}
    >
      <article className="container-fluid">
        <div className="p-5 pb-0 mt-1">
          <span
            className="linkClick"
            role="button"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={20} /> Back
          </span>{" "}
          / <span>Payment Options</span>
        </div>
      </article>

      <Container className="py-5 w-100">
        <Row className="bg-white py-5">
          <Col>
            <div className="">
              {/* NSF Header Alert Section */}
              {nsfData && (
                <div className="nsf-header-alert">
                  <div className="nsf-alert-icon">
                    <ExclamationTriangle size={24} />
                  </div>
                  <div className="nsf-alert-content">
                    <div className="nsf-alert-title">
                      Payment Failed - Insufficient Funds
                    </div>
                    <div className="nsf-alert-message">
                      Dear {borrowerName}, your automatic payment could not be
                      processed
                    </div>
                  </div>
                </div>
              )}

              {/* NSF Warning Section */}
              {nsfData && (
                <div className="nsf-warning-section">
                  <div className="nsf-warning-icon">
                    <ExclamationTriangle size={20} />
                  </div>
                  <div className="nsf-warning-content">
                    <div className="nsf-warning-title">
                      Non-Sufficient Funds (NSF)
                    </div>
                    <div className="nsf-warning-message">
                      Your payment attempt on {failureDate} was declined due to
                      insufficient funds in your account ending in{" "}
                      {maskedAccount.replace("****", "")}.
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Details Section */}
              {nsfData && (
                <div className="nsf-payment-details">
                  <h3 className="nsf-section-title">Payment Details</h3>

                  <div className="nsf-payment-grid">
                    <div className="nsf-payment-item">
                      <div className="nsf-payment-icon nsf-payment-icon-blue">
                        <Person size={20} />
                      </div>
                      <div className="nsf-payment-info">
                        <div className="nsf-payment-label">Borrower Name</div>
                        <div className="nsf-payment-value">{borrowerName}</div>
                      </div>
                    </div>

                    <div className="nsf-payment-item">
                      <div className="nsf-payment-icon nsf-payment-icon-blue">
                        <FileEarmark size={20} />
                      </div>
                      <div className="nsf-payment-info">
                        <div className="nsf-payment-label">Loan Number</div>
                        <div className="nsf-payment-value">{loanNumber}</div>
                      </div>
                    </div>

                    <div className="nsf-payment-item">
                      <div className="nsf-payment-icon nsf-payment-icon-red">
                        <Calendar size={20} />
                      </div>
                      <div className="nsf-payment-info">
                        <div className="nsf-payment-label">NSF Date</div>
                        <div className="nsf-payment-value">{failureDate}</div>
                      </div>
                    </div>

                    <div className="nsf-payment-item">
                      <div className="nsf-payment-icon nsf-payment-icon-green">
                        <CurrencyDollar size={20} />
                      </div>
                      <div className="nsf-payment-info">
                        <div className="nsf-payment-label">
                          NSF Payment Amount
                        </div>
                        <div className="nsf-payment-value">
                          {failedAmount !== null
                            ? isChecked
                              ? `${failedAmount} + $45 (NSF Fee) + $${nsfData?.LateCharges} (Late Charges) = ${nsfData?.amountIncludingNSFFee}`
                              : failedAmount
                            : "N/A"}
                        </div>
                      </div>
                    </div>

                    <div className="nsf-payment-item">
                      <div className="nsf-payment-icon nsf-payment-icon-gray">
                        <CreditCard size={20} />
                      </div>
                      <div className="nsf-payment-info">
                        <div className="nsf-payment-label">Account Used</div>
                        <div className="nsf-payment-value">{maskedAccount}</div>
                      </div>
                    </div>

                    <div className="nsf-payment-item">
                      <div className="nsf-payment-icon nsf-payment-icon-blue">
                        <Calendar size={20} />
                      </div>
                      <div className="nsf-payment-info">
                        <div className="nsf-payment-label">Next Due Date</div>
                        <div className="nsf-payment-value">{nextDueDate}</div>
                      </div>
                    </div>

                    <div className="nsf-payment-item">
                      <div className="nsf-payment-icon nsf-payment-icon-warning">
                        <ExclamationTriangle size={20} />
                      </div>
                      <div className="nsf-payment-info">
                        <div className="nsf-payment-label">NSF Fee</div>
                        <div className="nsf-payment-value nsf-fee-value">
                          ${nsfFee}
                        </div>
                      </div>
                    </div>
                    <div className="nsf-payment-item">
                      <div className="nsf-payment-icon nsf-payment-icon-warning">
                        <ExclamationTriangle size={20} />
                      </div>
                      <div className="nsf-payment-info">
                        <div className="nsf-payment-label">Late Charges</div>
                        <div className="nsf-payment-value nsf-fee-value">
                          ${nsfData?.LateCharges}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="px-4" style={{ marginTop: "20px" }}>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => setIsChecked(e.target.checked)}
                        className="form-check-input"
                        style={{
                          width: "18px",
                          height: "18px",
                          marginTop: "3px",
                          cursor: "pointer",
                        }}
                      />
                      <span
                        style={{
                          fontSize: "18px",
                          color: "#2c3e50",
                          lineHeight: 1.5,
                        }}
                      >
                        A <strong>$45</strong> NSF (Non-Sufficient Funds) fee
                        and any applicable late fees will be applied to your
                        payment.
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {/* Important Notice Section */}
              {nsfData && (
                <div className="nsf-important-notice">
                  <div className="nsf-notice-icon">
                    <ExclamationTriangle size={20} />
                  </div>
                  <div className="nsf-notice-content">
                    <div className="nsf-notice-title">Important Notice</div>
                    <ul className="nsf-notice-list">
                      <li>A $45 NSF fee has been added to your account</li>
                      <li>
                        Your payment is now past due and may be subject to
                        additional late fees if not resolved within the grace
                        period.
                      </li>
                      <li>
                        Please take immediate action to avoid further penalties
                      </li>
                      <li>
                        Contact us if you need assistance setting up a payment
                      </li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Payment Selection Section */}
              <div className="p-5">
                <h3 className="text-center mb-4">
                  {nsfData
                    ? "Select Payment Type to Resolve NSF"
                    : "Select Payment Type"}
                </h3>

                {!nsfData && (
                  <p className="text-center mb-4">
                    Please select the type of payment you would like to make:
                  </p>
                )}

                {/* {nsfData && (
                  <div className="text-center mb-4">
                    <div className="nsf-urgency-message">
                      <ExclamationTriangle
                        size={16}
                        className="text-warning me-2"
                      />
                      <span className="text-warning fw-bold">
                        Urgent: Your payment is overdue. Same day processing
                        recommended.
                      </span>
                    </div>
                  </div>
                )} */}

                <Row className="mt-5 gy-4">
                  {/* Reorder cards for NSF - Same Day Payment first and emphasized */}
                  <Col md={6} className={nsfData ? "order-2" : ""}>
                    <Card
                      className="h-100 border-primary payment-option-card"
                      onClick={() =>
                        navigate("/nsf/one-time-payment", {
                          state: { nsfData: { ...nsfData, isChecked } },
                        })
                      }
                      role="button"
                    >
                      <Card.Body className="d-flex flex-column align-items-center text-center p-4">
                        <div className="icon-wrapper rounded-circle bg-light p-3 mb-3">
                          <CalendarDate size={30} className="text-primary" />
                        </div>
                        <Card.Title>One Time Payment</Card.Title>
                        <Card.Text>
                          Schedule a payment for a future date. Payments process
                          on the next business day.
                          {nsfData && (
                            <div className="text-warning mt-2 small">
                              ⚠️ Delayed processing may result in additional
                              fees
                            </div>
                          )}
                        </Card.Text>
                        <Button
                          variant="primary"
                          className="mt-auto w-100"
                          onClick={() => navigate("/nsf/one-time-payment")}
                        >
                          <CurrencyDollar className="me-2" size={16} />
                          Select
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={6} className={nsfData ? "order-1" : ""}>
                    <Card
                      className={`h-100 payment-option-card border-primary`}
                      onClick={() =>
                        navigate("/nsf/same-day-payment", {
                          state: { nsfData: { ...nsfData, isChecked } },
                        })
                      }
                      role="button"
                    >
                      {/* {nsfData && (
                        <div className="recommended-badge">
                          <span>⚡ RECOMMENDED</span>
                        </div>
                      )} */}
                      <Card.Body className="d-flex flex-column align-items-center text-center p-4">
                        {/* <div
                          className={`icon-wrapper rounded-circle p-3 mb-3 ${
                            nsfData ? "bg-success text-white" : "bg-light"
                          }`}
                        >
                          <Lightning
                            size={30}
                            className={nsfData ? "text-white" : "text-primary"}
                          />
                        </div> */}
                        <div className="icon-wrapper rounded-circle bg-light p-3 mb-3">
                          <CalendarDate size={30} className="text-primary" />
                        </div>
                        <Card.Title
                        // className={nsfData ? "text-success fw-bold" : ""}
                        >
                          Same Day Payment
                          {/* {nsfData && (
                            <span className="text-success ms-2">✓</span>
                          )} */}
                        </Card.Title>
                        <Card.Text>
                          Make a payment that will be processed today. Must be
                          submitted before 3:00 PM PT. A $30 service fee will
                          apply.
                          {/* {nsfData && (
                            <div className="text-success mt-2 small fw-bold">
                              ✅ Immediate resolution
                            </div>
                          )} */}
                        </Card.Text>
                        <Button
                          variant="primary"
                          className="mt-auto w-100"
                          onClick={() => navigate("/nsf/same-day-payment")}
                        >
                          <CurrencyDollar className="me-2" size={16} />
                          Select
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      <style>{`
        .payment-option-card {
          transition: all 0.2s ease;
          cursor: pointer;
          position: relative;
        }
        
        .payment-option-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1);
        }
        
        .icon-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        /* Urgency Message */
        .nsf-urgency-message {
          background: #fff3cd;
          border: 1px solid #ffeaa7;
          border-radius: 8px;
          padding: 12px 20px;
          display: inline-flex;
          align-items: center;
          animation: pulse-warning 2s infinite;
        }

        @keyframes pulse-warning {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }

        /* Recommended Card Styling */
        .recommended-card {
          position: relative;
          border-width: 3px !important;
          box-shadow: 0 8px 25px rgba(40, 167, 69, 0.2) !important;
        }

        .recommended-badge {
          position: absolute;
          top: -10px;
          right: -10px;
          background: linear-gradient(45deg, #28a745, #20c997);
          color: white;
          padding: 5px 12px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: bold;
          z-index: 10;
          box-shadow: 0 2px 10px rgba(40, 167, 69, 0.4);
          animation: badge-glow 2s infinite;
        }

        @keyframes badge-glow {
          0%, 100% { 
            box-shadow: 0 2px 10px rgba(40, 167, 69, 0.4);
            transform: scale(1);
          }
          50% { 
            box-shadow: 0 4px 20px rgba(40, 167, 69, 0.6);
            transform: scale(1.05);
          }
        }

        /* Pulsing Button Animation */
        .pulsing-button {
          animation: pulse-button 2s infinite;
          position: relative;
          overflow: hidden;
        }

        @keyframes pulse-button {
          0%, 100% { 
            box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7);
          }
          50% { 
            box-shadow: 0 0 0 10px rgba(40, 167, 69, 0);
          }
        }

        .pulsing-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          transition: left 0.5s;
        }

        .pulsing-button:hover::before {
          left: 100%;
        }

        /* Enhanced card hover for recommended */
        .recommended-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 15px 35px rgba(40, 167, 69, 0.3) !important;
        }

      `}</style>
      <Disclaimer
        show={showDisclaimer}
        handleClose={() => setShowDisclaimer(false)}
      />
    </section>
  );
};

export default PaymentSelection;

const Disclaimer = ({ show, handleClose }) => {
  return (
    <PopupWrapper
      title=""
      extraClass="width-800"
      // backdrop="static"
      Component={
        <Alert
          key={"info"}
          variant={"secondary"}
          className="fw-bold text-center text-info"
        >
          <ExclamationTriangle className="me-3" size={18} />
          You are making a partial payment. The remaining NSF Fee, along with
          any applicable late charges, will be carried forward and added to your
          next payment.
        </Alert>
      }
      toShow={show}
      closeEventHandler={handleClose}
      bodyStyle={{ backgroundColor: "#ffe0da" }}
    />
  );
};
